import { useState, useEffect, useRef } from 'react';
import type { Project } from '@/utils/pocketbase';
import { GenerationProgress } from '@/components/shared/GenerationProgress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import { useTranslation } from 'react-i18next';
interface MarketAnalysisModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (formData: FormData) => void;
  projects: Project[];
  initialData?: {
    project_id: string;
    keywords: string;
    competitors: string;
    advantage: string;
    comment: string;
  };
  isEditing?: boolean;
  isLoading?: boolean;
}

export interface FormData {
  project_id: string;
  keywords: string;
  competitors: string;
  advantage: string;
  comment: string;
}

export function MarketAnalysisModal({
  isOpen,
  onClose,
  onSubmit,
  projects,
  initialData,
  isEditing = false,
  isLoading = false
}: MarketAnalysisModalProps) {
  const { t } = useTranslation('market-analysis');
  const [formData, setFormData] = useState<FormData>({
    project_id: '',
    keywords: '',
    competitors: '',
    advantage: '',
    comment: ''
  });

  // Refs for all textarea fields
  const keywordsRef = useRef<HTMLTextAreaElement>(null);
  const competitorsRef = useRef<HTMLTextAreaElement>(null);
  const advantageRef = useRef<HTMLTextAreaElement>(null);
  const commentRef = useRef<HTMLTextAreaElement>(null);

  // Helper function to adjust textarea height
  const adjustHeight = (textarea: HTMLTextAreaElement | null) => {
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = textarea.scrollHeight + 'px';
    }
  };

  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    }
  }, [initialData]);

  // Adjust heights when formData changes
  useEffect(() => {
    adjustHeight(keywordsRef.current);
    adjustHeight(competitorsRef.current);
    adjustHeight(advantageRef.current);
    adjustHeight(commentRef.current);
  }, [formData]);

  const handleSubmit = () => {
    onSubmit(formData);
  };

  const isValid = formData.project_id && formData.keywords && formData.competitors;

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-[16px]"
      onClick={(e) => {
        // Close modal if clicking on backdrop (not on modal content)
        if (e.target === e.currentTarget && !isLoading) {
          onClose();
        }
      }}
    >
      <div className="bg-white rounded-[24px] p-[24px] md:p-[40px] max-w-[580px] w-full max-h-[90vh] overflow-y-auto">
        {isLoading ? (
          <GenerationProgress
            title={t('modal.progressTitle')}
            message={isEditing
              ? t('modal.progressMessageEdit')
              : t('modal.progressMessageCreate')}
          />
        ) : (
          // Form State
          <>
            {/* Header */}
            <div className="flex flex-col gap-[16px] mb-[32px]">
              <h2 className="font-manrope font-bold text-[32px] leading-[40px] text-[#0d0d0d]">
                {isEditing ? t('modal.titleEdit') : t('modal.titleCreate')}
              </h2>
              <p className="font-segoe text-[16px] leading-[24px] text-[#565858]">
                {t('modal.subtitle')}
              </p>
            </div>

            {/* Form Fields */}
            <div className="flex flex-col gap-[24px] mb-[32px]">
              {/* Project Selection */}
              <div className="flex flex-col gap-[4px]">
                <label className="font-segoe text-[14px] leading-[20px] text-[#565858]">
                  {t('modal.labelProduct')}
                </label>
                <Select
                  value={formData.project_id}
                  onValueChange={(value) => setFormData({ ...formData, project_id: value })}
                  disabled={isEditing}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('modal.placeholderProduct')} />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map(project => (
                      <SelectItem key={project.id} value={project.id}>{project.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Keywords */}
              <div className="flex flex-col gap-[4px]">
                <label className="font-segoe text-[14px] leading-[20px] text-[#565858]">
                  {t('modal.labelKeywords')}
                </label>
                <textarea
                  ref={keywordsRef}
                  value={formData.keywords}
                  onChange={(e) => setFormData({ ...formData, keywords: e.target.value })}
                  onInput={(e) => adjustHeight(e.currentTarget)}
                  rows={1}
                  placeholder={t('modal.placeholderKeywords')}
                  className="
                    w-full
                    bg-transparent border border-[#eeeeee] rounded-[16px] px-[16px] py-[12px]
                    font-segoe text-[16px] leading-[24px] text-[#0d0d0d]
                    placeholder:text-[#969696]
                    resize-none outline-none overflow-hidden
                  "
                />
              </div>

              {/* Competitors */}
              <div className="flex flex-col gap-[4px]">
                <label className="font-segoe text-[14px] leading-[20px] text-[#565858]">
                  {t('modal.labelCompetitors')}
                </label>
                <textarea
                  ref={competitorsRef}
                  value={formData.competitors}
                  onChange={(e) => setFormData({ ...formData, competitors: e.target.value })}
                  onInput={(e) => adjustHeight(e.currentTarget)}
                  rows={1}
                  placeholder={t('modal.placeholderCompetitors')}
                  className="
                    w-full
                    bg-transparent border border-[#eeeeee] rounded-[16px] px-[16px] py-[12px]
                    font-segoe text-[16px] leading-[24px] text-[#0d0d0d]
                    placeholder:text-[#969696]
                    resize-none outline-none overflow-hidden
                  "
                />
              </div>

              {/* Advantage */}
              <div className="flex flex-col gap-[4px]">
                <label className="font-segoe text-[14px] leading-[20px] text-[#565858]">
                  {t('modal.labelAdvantage')}
                </label>
                <textarea
                  ref={advantageRef}
                  value={formData.advantage}
                  onChange={(e) => setFormData({ ...formData, advantage: e.target.value })}
                  onInput={(e) => adjustHeight(e.currentTarget)}
                  rows={1}
                  placeholder={t('modal.placeholderAdvantage')}
                  className="
                    w-full
                    bg-transparent border border-[#eeeeee] rounded-[16px] px-[16px] py-[12px]
                    font-segoe text-[16px] leading-[24px] text-[#0d0d0d]
                    placeholder:text-[#969696]
                    resize-none outline-none overflow-hidden
                  "
                />
              </div>

              {/* Comment */}
              <div className="flex flex-col gap-[4px]">
                <label className="font-segoe text-[14px] leading-[20px] text-[#565858]">
                  {t('modal.labelComment')}
                </label>
                <textarea
                  ref={commentRef}
                  value={formData.comment}
                  onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                  onInput={(e) => adjustHeight(e.currentTarget)}
                  rows={1}
                  placeholder={t('modal.placeholderComment')}
                  className="
                    w-full
                    bg-transparent border border-[#eeeeee] rounded-[16px] px-[16px] py-[12px]
                    font-segoe text-[16px] leading-[24px] text-[#0d0d0d]
                    placeholder:text-[#969696]
                    resize-none outline-none overflow-hidden
                  "
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-[24px]">
              <button
                onClick={onClose}
                className="
                  flex-1
                  bg-transparent
                  border border-[var(--grey-accent,#eeeeee)] border-solid
                  text-[color:var(--black-text,#0d0d0d)]
                  px-[24px] py-[12px]
                  rounded-[16px]
                  font-segoe font-semibold text-[16px] leading-[24px]
                  text-center
                  hover:bg-[#2197f1] hover:text-white hover:border-[#2197f1]
                  transition-colors
                "
              >
                {t('modal.buttonClose')}
              </button>
              <button
                onClick={handleSubmit}
                disabled={!isValid}
                className={`
                  flex-1
                  rounded-[16px] px-[24px] py-[12px]
                  font-segoe font-semibold text-[16px] leading-[24px]
                  text-center
                  transition-colors
                  ${isValid
                    ? 'bg-[#2197f1] text-white hover:bg-[#1a7bc7]'
                    : 'bg-[rgba(33,151,241,0.2)] text-[#2197f1] cursor-not-allowed opacity-60'
                  }
                `}
              >
                {isEditing ? t('modal.buttonUpdate') : t('modal.buttonCreate')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
